/**
 * i18n Utility Functions
 *
 * Core utilities for translation and formatting.
 * Used by React hooks in the design-system-ui package.
 */

import { DEFAULT_LANGUAGE, LanguageCode } from '../enums/language';
import { en } from './translations/en';
import type { InterpolationParams, TranslationDictionary } from './types';

// ============================================================================
// TRANSLATION REGISTRY
// ============================================================================

const translations: Record<LanguageCode, TranslationDictionary> = {
  [LanguageCode.EN]: en as unknown as TranslationDictionary,
  [LanguageCode.FA]: en as unknown as TranslationDictionary, // Fallback to English until Farsi is available
};

// ============================================================================
// TRANSLATION FUNCTIONS
// ============================================================================

/**
 * Get translation for a key with optional interpolation or default value
 *
 * @param key - Translation key (e.g., 'common.save')
 * @param lang - Language code
 * @param paramsOrDefault - Interpolation parameters or default value string
 * @returns Translated string
 */
export function translate(
  key: string,
  lang: LanguageCode = DEFAULT_LANGUAGE,
  paramsOrDefault?: InterpolationParams | string,
): string {
  const dictionary = translations[lang] || translations[DEFAULT_LANGUAGE];

  // Handle nested keys (e.g., 'common.save')
  const value = getNestedValue(dictionary, key);

  if (value === undefined) {
    // If a default value string was provided, return it
    if (typeof paramsOrDefault === 'string') {
      return paramsOrDefault;
    }
    // Return key in development, empty string in production.
    // Guarded for browser bundles — `process` is undefined in Vite/browser
    // contexts, and an unguarded reference here ships into every portal's
    // first translation lookup as `process is not defined` PageError.
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.warn(`Missing translation: ${key}`);
    }
    return key;
  }

  // Handle interpolation (only if params object was provided)
  if (paramsOrDefault && typeof paramsOrDefault === 'object' && typeof value === 'string') {
    return interpolate(value, paramsOrDefault);
  }

  return typeof value === 'string' ? value : key;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: TranslationDictionary, path: string): string | undefined {
  const value = obj[path];
  if (typeof value === 'string') {
    return value;
  }
  return undefined;
}

/**
 * Interpolate parameters into string
 * Supports {{param}} syntax
 */
function interpolate(str: string, params: InterpolationParams): string {
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : `{{${key}}}`;
  });
}

/**
 * Check if a translation exists
 */
export function hasTranslation(key: string, lang: LanguageCode = DEFAULT_LANGUAGE): boolean {
  const dictionary = translations[lang] || translations[DEFAULT_LANGUAGE];
  return getNestedValue(dictionary, key) !== undefined;
}

/**
 * Create a translation function bound to a specific language
 */
export function createTranslator(lang: LanguageCode) {
  return (key: string, paramsOrDefault?: InterpolationParams | string) => translate(key, lang, paramsOrDefault);
}

// ============================================================================
// FORMATTING FUNCTIONS
// ============================================================================

const LOCALE_MAP: Record<LanguageCode, string> = {
  [LanguageCode.EN]: 'en-US',
  [LanguageCode.FA]: 'fa-IR',
};

/**
 * Get locale string for Intl APIs
 */
export function getLocale(lang: LanguageCode): string {
  return LOCALE_MAP[lang] || 'en-US';
}

/**
 * Format date according to locale
 */
export function formatDate(
  date: Date | string,
  lang: LanguageCode = DEFAULT_LANGUAGE,
  format: 'short' | 'medium' | 'long' = 'medium',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = getLocale(lang);

  const formatOptions = {
    short: { month: 'short', day: 'numeric' },
    medium: { year: 'numeric', month: 'short', day: 'numeric' },
    long: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
  } as const satisfies Record<string, Intl.DateTimeFormatOptions>;

  const options = formatOptions[format];

  return d.toLocaleDateString(locale, options);
}

/**
 * Format date and time
 */
export function formatDateTime(date: Date | string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = getLocale(lang);

  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format number according to locale
 */
export function formatNumber(
  num: number,
  lang: LanguageCode = DEFAULT_LANGUAGE,
  options?: Intl.NumberFormatOptions,
): string {
  const locale = getLocale(lang);
  return num.toLocaleString(locale, options);
}

/**
 * Format currency
 */
export function formatCurrency(
  amount: number,
  lang: LanguageCode = DEFAULT_LANGUAGE,
  currency: string = 'USD',
): string {
  const locale = getLocale(lang);
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
  });
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(date: Date | string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  const locale = getLocale(lang);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSecs) < 60) {
    return rtf.format(diffSecs, 'second');
  }
  if (Math.abs(diffMins) < 60) {
    return rtf.format(diffMins, 'minute');
  }
  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day');
  }
  if (Math.abs(diffDays) < 365) {
    return rtf.format(Math.round(diffDays / 30), 'month');
  }
  return rtf.format(Math.round(diffDays / 365), 'year');
}

// ============================================================================
// ENUMS TO LABELS
// ============================================================================

/**
 * Get translated label for case status
 */
export function getCaseStatusLabel(status: string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const key = `status.case.${status}`;
  return hasTranslation(key, lang) ? translate(key, lang) : status;
}

/**
 * Get translated label for lead status
 */
export function getLeadStatusLabel(status: string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const key = `status.lead.${status}`;
  return hasTranslation(key, lang) ? translate(key, lang) : status;
}

/**
 * Get translated label for priority
 */
export function getPriorityLabel(priority: string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const key = `status.priority.${priority}`;
  return hasTranslation(key, lang) ? translate(key, lang) : priority;
}

/**
 * Get translated label for document status
 */
export function getDocumentStatusLabel(status: string, lang: LanguageCode = DEFAULT_LANGUAGE): string {
  const key = `status.document.${status}`;
  return hasTranslation(key, lang) ? translate(key, lang) : status;
}
